import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Button, Tag } from "../../../../../design-system";
import {
  AdSwipeDoc,
  swipeRunDays,
} from "../../../../../database/community/adEngine";
import { useMediaStudio } from "../../context/MediaStudioContext";
import { Card, CardHint, EmptyState, Muted, Row, RowList, SectionRoot, Spacer, Stat, StatLabel, StatValue } from "../shared";
import Modal from "../Modal";
import { useAdEngine } from "./useAdEngine";
import SwipeDetailModal from "./SwipeDetailModal";
import CampaignPlanPanel from "./CampaignPlanPanel";

// The ads workflow dashboard: what competitors are running (swipe file with
// run-length tracking), who is being watched, and the path from a proven
// competitor ad into the ads generator. Data is written by the weekly ad spy
// run; this screen reads it and triages.

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--space-2);
`;

const Tile = styled.div`
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  background: var(--color-surface-subtle);
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
`;

const FilterChip = styled.button<{ $on: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid
    ${(p) => (p.$on ? "var(--color-border-strong)" : "var(--color-border-subtle)")};
  background: ${(p) => (p.$on ? "var(--color-surface-subtle)" : "transparent")};
  color: var(--color-text);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: var(--space-2);
`;

const SwipeCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--space-2);
  padding: var(--space-3);
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: var(--color-border-strong);
  }
`;

const Advertiser = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AdCopy = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 3em;
`;

const STATUS_FILTERS = ["all", "proven", "new", "shortlisted", "recreated", "dismissed"] as const;

// An ad younger than this has not paid for itself yet. Copy the old standing
// ads, not yesterday's experiments.
export const PROVEN_RUN_DAYS = 7;

const Thumb = styled.img`
  width: 100%;
  height: 130px;
  object-fit: cover;
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
`;

export type AdsDashboardProps = {
  onNavigate: (path: string) => void;
};

export const AdsDashboard: React.FC<AdsDashboardProps> = ({ onNavigate }) => {
  const { communityId } = useMediaStudio();
  const engine = useAdEngine(communityId);

  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>("all");
  const [advertiserFilter, setAdvertiserFilter] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showWatchlist, setShowWatchlist] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const advertisers = useMemo(
    () =>
      Array.from(new Set(engine.swipes.map((s) => s.advertiserName))).sort(),
    [engine.swipes]
  );

  // Longest running first: the whole point of the swipe file is surfacing
  // the old standing ads, not the newest experiments.
  const visible = useMemo(
    () =>
      engine.swipes
        .filter((s) => {
          if (statusFilter === "proven") {
            if (!s.active || swipeRunDays(s) < PROVEN_RUN_DAYS) return false;
          } else if (statusFilter !== "all" && s.status !== statusFilter) {
            return false;
          }
          if (advertiserFilter !== "all" && s.advertiserName !== advertiserFilter)
            return false;
          return true;
        })
        .sort((a, b) => swipeRunDays(b) - swipeRunDays(a)),
    [engine.swipes, statusFilter, advertiserFilter]
  );

  const stats = useMemo(() => {
    const active = engine.swipes.filter((s) => s.active);
    const longest = engine.swipes.reduce(
      (best, s) => Math.max(best, swipeRunDays(s)),
      0
    );
    return {
      active: active.length,
      watched: engine.config?.watchlist.length ?? 0,
      longest,
      shortlisted: engine.swipes.filter((s) => s.status === "shortlisted").length,
    };
  }, [engine.swipes, engine.config]);

  const selected = selectedId
    ? engine.swipes.find((s) => s.id === selectedId) || null
    : null;

  // Recreate handoff: shortlist so it survives triage, open the live ad so
  // the coach can capture the creative, land in the generator's reference
  // uploader. Ad library pages cannot be mirrored client-side.
  const handleRecreate = (swipe: AdSwipeDoc) => {
    engine.setStatus(swipe.id, "shortlisted");
    if (swipe.snapshotUrl) window.open(swipe.snapshotUrl, "_blank", "noopener");
    onNavigate("/media-studio/create?flow=ads");
  };

  const notOnboarded = !engine.loading && engine.config === null;

  return (
    <SectionRoot>
      {communityId && <CampaignPlanPanel key={communityId} communityId={communityId} onNavigate={onNavigate} />}
      {notOnboarded ? (
        <EmptyState>
          <div style={{ fontSize: 26 }}>🔍</div>
          <div>
            The ad spy is not set up yet. Ask Claude to seed a watchlist of
            competitor advertisers and it starts tracking their ads weekly.
          </div>
        </EmptyState>
      ) : (
        <>
          <Strip>
            <Tile>
              <Stat>
                <StatValue>{engine.loading ? "–" : stats.active}</StatValue>
                <StatLabel>Active competitor ads</StatLabel>
              </Stat>
            </Tile>
            <Tile>
              <Stat>
                <StatValue>{engine.loading ? "–" : stats.watched}</StatValue>
                <StatLabel>Advertisers watched</StatLabel>
              </Stat>
            </Tile>
            <Tile>
              <Stat>
                <StatValue>
                  {engine.loading ? "–" : `${stats.longest}d`}
                </StatValue>
                <StatLabel>Longest run</StatLabel>
              </Stat>
            </Tile>
            <Tile>
              <Stat>
                <StatValue>{engine.loading ? "–" : stats.shortlisted}</StatValue>
                <StatLabel>Shortlisted</StatLabel>
              </Stat>
            </Tile>
          </Strip>

          <Card>
            <Row style={{ borderTop: "none", padding: 0 }}>
              <div>
                <Advertiser as="div">Ad spy</Advertiser>
                <Muted>
                  {engine.report?.summary
                    ? engine.report.summary.split(". ")[0] + "."
                    : "No scan yet. The weekly run fills the swipe file."}
                </Muted>
              </div>
              <Spacer />
              {engine.report && (
                <Button variant="ghost" size="sm" onClick={() => setShowReport(true)}>
                  Last report
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setShowWatchlist(true)}>
                Watchlist
              </Button>
            </Row>
          </Card>

          <FilterRow>
            {STATUS_FILTERS.map((f) => (
              <FilterChip
                key={f}
                type="button"
                $on={statusFilter === f}
                onClick={() => setStatusFilter(f)}
              >
                {f === "all" ? "All" : f}
              </FilterChip>
            ))}
            <Spacer />
            <select
              value={advertiserFilter}
              onChange={(e) => setAdvertiserFilter(e.target.value)}
              style={{
                background: "var(--color-surface)",
                color: "var(--color-text)",
                border: "1px solid var(--color-border-subtle)",
                borderRadius: "var(--radius-md)",
                padding: "6px 8px",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--font-size-sm)",
              }}
            >
              <option value="all">All advertisers</option>
              {advertisers.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </FilterRow>

          {engine.loading ? (
            <CardHint>Loading the swipe file.</CardHint>
          ) : visible.length === 0 ? (
            <EmptyState>
              No swipes match this filter. New competitor ads land here after
              each weekly scan.
            </EmptyState>
          ) : (
            <Grid>
              {visible.map((s) => {
                const days = swipeRunDays(s);
                return (
                  <SwipeCard key={s.id} onClick={() => setSelectedId(s.id)}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <Advertiser style={{ flex: 1, minWidth: 0 }}>
                        {s.advertiserName}
                      </Advertiser>
                      <Tag
                        variant={
                          days >= PROVEN_RUN_DAYS && s.active ? "success" : "neutral"
                        }
                        size="sm"
                      >
                        {s.active ? `${days}d` : "stopped"}
                      </Tag>
                    </div>
                    {s.storedCreatives[0] &&
                      s.storedCreatives[0].contentType.startsWith("image/") && (
                        <Thumb src={s.storedCreatives[0].url} alt="" />
                      )}
                    <AdCopy>{s.title || s.bodyText || "No copy captured."}</AdCopy>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Tag variant="neutral" size="sm">
                        {s.platform === "meta" ? "Meta" : s.platform}
                      </Tag>
                      {s.status !== "new" && (
                        <Tag
                          variant={
                            s.status === "recreated"
                              ? "success"
                              : s.status === "shortlisted"
                              ? "warning"
                              : "neutral"
                          }
                          size="sm"
                        >
                          {s.status}
                        </Tag>
                      )}
                    </div>
                  </SwipeCard>
                );
              })}
            </Grid>
          )}
        </>
      )}

      {selected && (
        <SwipeDetailModal
          swipe={selected}
          onClose={() => setSelectedId(null)}
          onSetStatus={engine.setStatus}
          onRecreate={handleRecreate}
        />
      )}

      {showWatchlist && engine.config && (
        <Modal title="Watchlist" onClose={() => setShowWatchlist(false)}>
          <RowList>
            {engine.config.watchlist.map((w, i) => (
              <Row key={`${w.name || i}`}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Advertiser as="div">{w.name || "Unnamed"}</Advertiser>
                  {w.notes && <Muted>{w.notes}</Muted>}
                </div>
                <Tag variant="neutral" size="sm">
                  {w.platform || "meta"}
                </Tag>
              </Row>
            ))}
          </RowList>
          {engine.config.keywords.length > 0 && (
            <CardHint>
              Keyword scans: {engine.config.keywords.join(", ")}.
            </CardHint>
          )}
          <CardHint>
            To add or remove competitors, ask Claude to update the ad engine
            watchlist. The scan runs every Monday morning.
          </CardHint>
        </Modal>
      )}

      {showReport && engine.report && (
        <Modal title={`Scan report ${engine.report.id}`} onClose={() => setShowReport(false)}>
          {engine.report.summary && <CardHint>{engine.report.summary}</CardHint>}
          {Array.isArray(engine.report.advertisers) &&
            engine.report.advertisers.length > 0 && (
              <RowList>
                {engine.report.advertisers.map((a, i) => (
                  <Row key={`${a.name || i}`}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Advertiser as="div">{a.name}</Advertiser>
                    </div>
                    <Muted>
                      {[
                        typeof a.activeAds === "number" && `${a.activeAds} active`,
                        typeof a.newAds === "number" && `${a.newAds} new`,
                        typeof a.goneAds === "number" && `${a.goneAds} gone`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Muted>
                  </Row>
                ))}
              </RowList>
            )}
        </Modal>
      )}
    </SectionRoot>
  );
};

export default AdsDashboard;
